import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Post, Comment } from '../../models/Post';
import { User } from '../../models/User';
import { HeartIcon as HeartOutline, ChatAltIcon, TrashIcon, ChevronLeftIcon, ChevronRightIcon, PaperAirplaneIcon, RefreshIcon, VolumeUpIcon, VolumeOffIcon, XIcon, ShieldExclamationIcon } from '@heroicons/react/outline';
import { HeartIcon as HeartSolid, StarIcon } from '@heroicons/react/solid';
import { motion, AnimatePresence } from 'framer-motion';
import { formatRelativeTime } from '../../utils/DateFormatter';
import { S3Client } from '@aws-sdk/client-s3';
import { FeedService } from '../../utils/FeedService';
import { AttributeValue } from '@aws-sdk/client-dynamodb';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { throttle } from 'lodash';
import { recordTelemetry } from '../../utils/TelemetryService';

interface PostCardProps {
  post: Post;
  s3Client: S3Client;
  feedService: FeedService;
  poster?: User;
  currentUser: User;
  allUsers: User[];
  isAdmin?: boolean;
  onLikeToggle: (postId: string) => void;
  onDelete?: (post: Post) => void;
  onCommentChange?: (postId: string, newCount: number, latestComment?: Comment, metadataUpdate?: Partial<Post>) => void;
}

const VideoPlayer: React.FC<{ url: string; isActive: boolean; isFullscreen?: boolean; onFullscreenExit?: () => void }> = ({ url, isActive, isFullscreen = false, onFullscreenExit }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(!isFullscreen);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleFullscreenExit = () => {
      // Small delay to allow iOS to restore the viewport/layout
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        if (onFullscreenExit) onFullscreenExit();
      }, 150);
    };

    // iOS native fullscreen events
    video.addEventListener('webkitendfullscreen', handleFullscreenExit);
    // Standard fullscreen events
    document.addEventListener('fullscreenchange', handleFullscreenExit);

    return () => {
      video.removeEventListener('webkitendfullscreen', handleFullscreenExit);
      document.removeEventListener('fullscreenchange', handleFullscreenExit);
    };
  }, [onFullscreenExit]);

  useEffect(() => {
    if (videoRef.current) {
      if (isActive) {
        videoRef.current.play().catch(() => {
          if (videoRef.current) {
            videoRef.current.muted = true;
            videoRef.current.play();
          }
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [isActive]);

  return (
    <div className={`relative w-full h-full ${isFullscreen ? 'bg-transparent' : 'bg-black'} flex items-center justify-center ${!hasLoaded && !isFullscreen ? 'shimmer' : ''}`}>
      <video
        ref={videoRef}
        src={url}
        onLoadedData={() => setHasLoaded(true)}
        className={`${isFullscreen ? 'max-w-full max-h-full object-contain' : 'max-w-full max-h-full'} transition-opacity duration-300 ${hasLoaded ? 'opacity-100' : 'opacity-0'}`}
        loop
        playsInline
        webkit-playsinline="true"
        muted={isMuted}
        controls={isFullscreen}
        controlsList="nofullscreen"
        disablePictureInPicture
      />
      {!isFullscreen && (
        <button 
          onClick={(e) => {
            e.stopPropagation();
            setIsMuted(!isMuted);
          }}
          className="absolute bottom-4 right-4 p-2 bg-black/60 rounded-full text-white hover:bg-black/80 transition-[background-color,transform] z-20 border border-white/10"
        >
          {isMuted ? <VolumeOffIcon className="h-4 w-4" /> : <VolumeUpIcon className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
};

const PostImage: React.FC<{ url: string; s3Key: string; isMobile: boolean; onLoaded: (key: string) => void; isLoaded: boolean; hideBlur?: boolean }> = ({ url, s3Key, isMobile, onLoaded, isLoaded, hideBlur = false }) => {
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!isLoaded && imgRef.current?.complete) {
      onLoaded(s3Key);
    }
  }, [url, s3Key, isLoaded]); // Removed onLoaded to prevent render loops

  return (
    <img 
      ref={imgRef}
      src={url} 
      alt="post media" 
      draggable={false} 
      decoding="async"
      onLoad={() => onLoaded(s3Key)}
      className={`relative z-10 max-w-full max-h-full object-contain transition-opacity duration-300 pointer-events-none ${isLoaded ? 'opacity-100' : 'opacity-0'}`} 
    />
  );
};

export const PostCard: React.FC<PostCardProps> = React.memo(({ post, feedService, poster, currentUser, allUsers, isAdmin, onLikeToggle, onDelete, onCommentChange }) => {
  const isMobile = window.innerWidth < 768;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [loadedImages, setMediaLoaded] = useState<Set<string>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<Comment[]>(() => 
    [...(post.recentComments || [])].sort((a, b) => a.createdAtEpochMilli - b.createdAtEpochMilli)
  );
  const [commentCount, setCommentCount] = useState(post.commentCount || 0);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [hasLoadedFullComments, setHasLoadedFullComments] = useState(false);
  const [nextKey, setNextKey] = useState<Record<string, AttributeValue> | undefined>(undefined);
  const [commentToDelete, setCommentToDelete] = useState<Comment | null>(null);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isPinching, setIsPinching] = useState(false);
  
  const postRef = useRef<HTMLDivElement>(null);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isOwnPost = currentUser?.cognitoUserId === post.posterCognitoUserId;
  const posterName = poster ? `${poster.firstName} ${poster.lastName}` : 'Unknown Member';
  const posterInitials = poster ? `${poster.firstName.charAt(0)}${poster.lastName.charAt(0)}` : '?';

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (showComments && isMobile) {
      // Small delay to allow the animation to finish
      const timer = setTimeout(() => {
        if (commentInputRef.current) {
          commentInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [showComments, isMobile]);

  useEffect(() => {
    setCommentCount(post.commentCount || 0);
  }, [post.commentCount]);

  useEffect(() => {
    if (isFullscreenOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setIsZoomed(false);
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isFullscreenOpen]);

  const handleScroll = useCallback(throttle(() => {
    if (!scrollRef.current || isFullscreenOpen) return;
    const { scrollLeft, clientWidth } = scrollRef.current;
    if (clientWidth <= 0) return;
    
    const newIndex = Math.round(scrollLeft / clientWidth);
    const maxIndex = (post.s3MediaKeys?.length || 1) - 1;
    const clampedIndex = Math.max(0, Math.min(newIndex, maxIndex));
    
    setCurrentIndex(prev => {
      if (prev !== clampedIndex) return clampedIndex;
      return prev;
    });
  }, 100), [isFullscreenOpen, post.s3MediaKeys?.length]);

  const paginate = useCallback((newDirection: number) => {
    if (!post.s3MediaKeys) return;
    
    setCurrentIndex(prev => {
      const nextIndex = prev + newDirection;
      if (nextIndex >= 0 && nextIndex < post.s3MediaKeys!.length) {
        // Synchronize the inline scroll if it exists
        if (scrollRef.current) {
          scrollRef.current.scrollTo({
            left: nextIndex * scrollRef.current.clientWidth,
            behavior: 'smooth'
          });
        }
        setIsZoomed(false);
        return nextIndex;
      }
      return prev;
    });
  }, [post.s3MediaKeys]);

  // Keyboard support for fullscreen
  useEffect(() => {
    if (!isFullscreenOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') paginate(-1);
      else if (e.key === 'ArrowRight') paginate(1);
      else if (e.key === 'Escape') setIsFullscreenOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreenOpen, paginate]);

  const fetchFullComments = useCallback(async (isLoadMore = false, isInitialOpen = false) => {
    if (isLoadingComments && !isInitialOpen) return;
    setIsLoadingComments(true);
    try {
      // Fetch latest metadata only on initial open to ensure count and top comment are accurate
      const [result, latestMetadata] = await Promise.all([
        feedService.getComments(post.id, 5, isLoadMore ? nextKey : undefined),
        (isLoadMore || !feedService.getPostMetadata) ? Promise.resolve(null) : feedService.getPostMetadata(post.id)
      ]);

      if (latestMetadata) {
        setCommentCount(latestMetadata.commentCount || 0);
        // Synchronize parent state so the global feed list remains fresh (including top comment)
        if (onCommentChange) {
          onCommentChange(post.id, latestMetadata.commentCount || 0, undefined, {
            topComment: latestMetadata.topComment,
            recentComments: latestMetadata.recentComments
          });
        }
      }

      const withLikeStatus = await Promise.all(result.comments.map(async (c) => {
        const isLiked = await feedService.checkIfCommentLiked(post.id, c.id, currentUser.cognitoUserId);
        return { ...c, isLiked };
      }));

      setComments(prev => {
        const combined = isLoadMore ? [...prev, ...withLikeStatus] : withLikeStatus;
        // Always sort oldest first for consistent bottom-to-top stream
        return [...combined].sort((a, b) => a.createdAtEpochMilli - b.createdAtEpochMilli);
      });
      
      setNextKey(result.nextKey);
      if (!isLoadMore) setHasLoadedFullComments(true);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setIsLoadingComments(false);
    }
  }, [post.id, feedService, currentUser.cognitoUserId, nextKey, isLoadingComments, onCommentChange]);

  const handleToggleComments = () => {
    const willShow = !showComments;
    
    if (!willShow && isMobile && postRef.current) {
      const rect = postRef.current.getBoundingClientRect();
      if (rect.top < 0) {
        // Capture current scroll before state change
        const scrollY = window.scrollY;
        const offset = rect.top;
        
        setShowComments(false);
        
        // Immediately adjust scroll to 'lock' the post in place visually
        // while it starts to shrink from below
        window.scrollTo({
          top: scrollY + offset - 10,
          behavior: 'auto' // Instant for better stability during layout change
        });
        return;
      }
    }
    
    setShowComments(willShow);
    if (willShow) {
      // Set loading state IMMEDIATELY to prevent the "flash" of old/unsorted comments
      if (!hasLoadedFullComments) {
        setIsLoadingComments(true);
      }
      fetchFullComments(false, true);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const added = await feedService.addComment(post.id, currentUser.cognitoUserId, newComment);
      recordTelemetry('CommentAdded', { postId: post.id });
      const updatedComments = [...comments, { ...added, isLiked: false, likeCount: 0 }].sort((a, b) => a.createdAtEpochMilli - b.createdAtEpochMilli);
      setComments(updatedComments);
      const newCount = commentCount + 1;
      setCommentCount(newCount);
      setNewComment('');
      
      if (onCommentChange) {
        onCommentChange(post.id, newCount, added);
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;
    const commentId = commentToDelete.id;
    const createdAt = commentToDelete.createdAtEpochMilli;
    
    const updatedComments = comments.filter(c => c.id !== commentId);
    setComments(updatedComments);
    const newCount = Math.max(0, commentCount - 1);
    setCommentCount(newCount);
    setCommentToDelete(null);

    if (onCommentChange) {
      const likedComments = updatedComments.filter(c => (c.likeCount || 0) > 0);
      const top = likedComments.length > 0 
        ? likedComments.reduce((prevTop, curr) => ((prevTop.likeCount || 0) > (curr.likeCount || 0)) ? prevTop : curr)
        : undefined;

      onCommentChange(post.id, newCount, undefined, { topComment: top });
    }

    try {
      await feedService.deleteComment(post.id, commentId, createdAt);
    } catch (error) {
      console.error('Failed to delete comment:', error);
      fetchFullComments();
    }
  };

  const handleCommentLikeToggle = async (comment: Comment) => {
    const isCurrentlyLiked = !!comment.isLiked;
    
    const updateLocalComments = (prev: Comment[]) => {
      const updated = prev.map(c => c.id === comment.id ? { 
        ...c, 
        isLiked: !isCurrentlyLiked,
        likeCount: (c.likeCount || 0) + (isCurrentlyLiked ? -1 : 1)
      } : c);

      // Recalculate top comment for UI preview
      if (onCommentChange) {
        const likedComments = updated.filter(c => (c.likeCount || 0) > 0);
        const top = likedComments.length > 0 
          ? likedComments.reduce((prevTop, curr) => ((prevTop.likeCount || 0) > (curr.likeCount || 0)) ? prevTop : curr)
          : undefined;
        
        onCommentChange(post.id, commentCount, undefined, { topComment: top });
      }

      return updated;
    };

    setComments(updateLocalComments);
    recordTelemetry(isCurrentlyLiked ? 'CommentUnliked' : 'CommentLiked', { postId: post.id, commentId: comment.id });

    try {
      await feedService.toggleCommentLike(post.id, comment.id, comment.createdAtEpochMilli, currentUser.cognitoUserId, isCurrentlyLiked);
    } catch (error) {
      console.error('Failed to toggle comment like:', error);
      const rollbackLocalComments = (prev: Comment[]) => {
        const rolledBack = prev.map(c => c.id === comment.id ? { 
          ...c, 
          isLiked: isCurrentlyLiked,
          likeCount: (c.likeCount || 0) + (isCurrentlyLiked ? 1 : -1)
        } : c);

        if (onCommentChange) {
          const likedComments = rolledBack.filter(c => (c.likeCount || 0) > 0);
          const top = likedComments.length > 0 
            ? likedComments.reduce((prevTop, curr) => ((prevTop.likeCount || 0) > (curr.likeCount || 0)) ? prevTop : curr)
            : undefined;
          
          onCommentChange(post.id, commentCount, undefined, { topComment: top });
        }

        return rolledBack;
      };
      setComments(rollbackLocalComments);
    }
  };

  const getCommenter = (userId: string) => {
    return allUsers.find(u => u.cognitoUserId === userId);
  };

  const isVideo = (key: string) => {
    const ext = key.split('.').pop()?.toLowerCase();
    return ext === 'mp4' || ext === 'mov';
  };

  return (
    <motion.div 
      ref={postRef}
      initial={isMobile ? { opacity: 1 } : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ 
        opacity: { duration: 0.3 }
      }}
      className={`bg-white dark:bg-slate-800 rounded-[2rem] sm:rounded-[2.5rem] shadow-sm border overflow-hidden mb-6 sm:mb-8 transition-colors ${
        isOwnPost 
        ? 'border-blue-500/30 dark:border-blue-500/40 ring-1 ring-blue-500/10 dark:ring-blue-500/20' 
        : 'border-slate-200/60 dark:border-slate-700/50'
      }`}
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-full font-bold flex items-center justify-center ${
            isOwnPost ? 'bg-blue-600 text-white shadow-sm' : 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400'
          }`}>
            {posterInitials}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-slate-900 dark:text-white leading-tight">{posterName}</h4>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">{formatRelativeTime(post.createdAtEpochMilli)}</p>
          </div>
        </div>
        {(isOwnPost || isAdmin) && onDelete && (
          <button 
            onClick={() => onDelete(post)} 
            className={`p-2 rounded-full transition-all ${
              isOwnPost 
                ? 'text-slate-400 md:hover:text-red-500 md:hover:bg-red-50 md:dark:hover:bg-red-900/20' 
                : 'text-amber-500 md:hover:text-amber-600 md:hover:bg-amber-50 md:dark:hover:bg-amber-900/20'
            }`}
            title={isOwnPost ? 'Delete Post' : 'Admin: Delete Post'}
          >
            {isOwnPost ? <TrashIcon className="h-5 w-5" /> : <ShieldExclamationIcon className="h-5 w-5" />}
          </button>
        )}
      </div>

      <div className="px-4 pb-3">
        <p className="text-slate-800 dark:text-slate-100 whitespace-pre-wrap leading-relaxed break-words">{post.content}</p>
      </div>

      {/* Media Carousel - NATIVE CSS SCROLL SNAP FOR PERFORMANCE */}
      {post.s3MediaKeys && post.s3MediaKeys.length > 0 && post.signedMediaUrls && (
        <div className="relative bg-slate-50 dark:bg-slate-950 overflow-hidden group">
          <div 
            ref={scrollRef}
            onScroll={handleScroll}
            onClick={() => {
              recordTelemetry('FeedImageViewed', { postId: post.id, s3Key: post.s3MediaKeys?.[currentIndex] });
              setIsFullscreenOpen(true);
            }}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide cursor-zoom-in touch-pan-x touch-pan-y"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {post.signedMediaUrls.map((url, index) => {
              const key = post.s3MediaKeys![index];
              const isMediaVideo = isVideo(key);
              return (
                <div key={key} className={`w-full flex-shrink-0 snap-center snap-always relative overflow-hidden flex items-center justify-center bg-slate-200 dark:bg-slate-900 aspect-square sm:aspect-video max-h-[600px] select-none ${!loadedImages.has(key) && !isMediaVideo ? 'shimmer' : ''}`}>
                  {url && (
                    <>
                      {isMediaVideo ? (
                        <VideoPlayer url={url} isActive={currentIndex === index} />
                      ) : (
                        <PostImage 
                          url={url} 
                          s3Key={key} 
                          isMobile={isMobile} 
                          isLoaded={loadedImages.has(key)}
                          onLoaded={(k) => setMediaLoaded(prev => new Set(prev).add(k))}
                        />
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {post.s3MediaKeys.length > 1 && (
            <>
              {currentIndex > 0 && (
                <button onClick={(e) => { e.stopPropagation(); paginate(-1); }} className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-[background-color,transform] z-10 shadow-lg border border-white/20 hidden sm:block">
                  <ChevronLeftIcon className="h-6 w-6" />
                </button>
              )}
              {currentIndex < post.s3MediaKeys.length - 1 && (
                <button onClick={(e) => { e.stopPropagation(); paginate(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-[background-color,transform] z-10 shadow-lg border border-white/20 hidden sm:block">
                  <ChevronRightIcon className="h-6 w-6" />
                </button>
              )}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10 bg-black/40 px-3 py-2 rounded-full border border-white/10 shadow-xl pointer-events-none">
                {post.s3MediaKeys.map((_, i) => (
                  <div key={i} className={`h-1.5 rounded-full transition-all duration-300 shadow-sm ${i === currentIndex ? 'bg-white w-4' : 'bg-white/40 w-1.5'}`} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Interactions Bar */}
      <div className="p-2 px-4 flex items-center gap-6 border-t border-slate-100 dark:border-slate-700/50">
        <button 
          onClick={() => {
            recordTelemetry(post.isLikedByCurrentUser ? 'PostUnliked' : 'PostLiked', { postId: post.id });
            onLikeToggle(post.id);
          }} 
          className="flex items-center gap-2 group py-2"
        >
          <div className="relative">
            <AnimatePresence mode="wait">
              {post.isLikedByCurrentUser ? (
                <motion.div key="liked" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
                  <HeartSolid className="h-6 w-6 text-rose-500" />
                </motion.div>
              ) : (
                <motion.div key="unliked" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
                  <HeartOutline className="h-6 w-6 text-slate-400 md:group-hover:text-rose-500 transition-colors" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <span className={`text-sm font-semibold ${post.isLikedByCurrentUser ? 'text-rose-500' : 'text-slate-500 dark:text-slate-400'}`}>{post.likeCount || 0}</span>
        </button>
        <button onClick={handleToggleComments} className="flex items-center gap-2 group py-2">
          <ChatAltIcon className={`h-6 w-6 transition-colors ${showComments ? 'text-blue-600' : 'text-slate-400 md:group-hover:text-blue-500'}`} />
          <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">{commentCount}</span>
        </button>
      </div>

      {/* Top Comment Preview - FAIL-SAFE: ONLY SHOW IF LIKES > 0 */}
      {!showComments && post.topComment && (post.topComment.likeCount || 0) > 0 && (
        <div className="px-4 pb-4">
          <div className="bg-slate-50/80 dark:bg-slate-900/40 rounded-2xl p-3 border border-slate-100 dark:border-slate-800/50">
            <div className="flex items-center gap-1.5 mb-2">
              <StarIcon className="h-3 w-3 text-amber-500" />
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Popular Comment</span>
            </div>
            <div className="flex items-start gap-2">
              <div className="flex-grow min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5 overflow-hidden">
                  <div className="h-4 w-4 rounded-full bg-blue-600 flex items-center justify-center text-[8px] font-bold text-white shrink-0">
                    {getCommenter(post.topComment.commentCognitoUserId)?.firstName.charAt(0)}
                  </div>
                  <span className="text-[10px] font-bold text-slate-900 dark:text-white truncate">
                    {getCommenter(post.topComment.commentCognitoUserId)?.firstName}
                  </span>
                </div>
                <p className="text-xs text-slate-700 dark:text-slate-300 line-clamp-1 truncate leading-relaxed mb-1.5">
                  {post.topComment.content}
                </p>
              </div>
              <span className="text-[10px] text-slate-400 flex items-center gap-1 shrink-0 mt-1">
                <HeartSolid className="h-2.5 w-2.5 text-rose-400" />
                {post.topComment.likeCount}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Inline Comments Section */}
      <AnimatePresence initial={false}>
        {showComments && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ 
              height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
              opacity: { duration: 0.2 }
            }}
            className="overflow-hidden bg-slate-50/50 dark:bg-slate-900/30 border-t border-slate-100 dark:border-slate-700/50"
          >
            <div className="p-4 space-y-4">
              {nextKey && (
                <button onClick={() => fetchFullComments(true)} className="w-full py-2 text-xs font-bold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-all mb-2 flex items-center justify-center gap-2">
                  {isLoadingComments ? <RefreshIcon className="h-4 w-4 animate-spin" /> : 'Load older comments...'}
                </button>
              )}
              <div className="space-y-4">
                {comments.length === 0 && (!isLoadingComments || commentCount === 0) && (
                  <p className="text-center py-4 text-xs text-slate-500 italic">No comments yet. Start the conversation!</p>
                )}
                {isLoadingComments && !hasLoadedFullComments && commentCount > 0 && (
                  <div className="space-y-4 animate-pulse">
                    {[1, 2].map(i => (
                      <div key={i} className="flex gap-3">
                        <div className="h-7 w-7 rounded-full bg-slate-200 dark:bg-slate-700 shrink-0" />
                        <div className="flex-grow h-12 rounded-2xl bg-slate-200 dark:bg-slate-700" />
                      </div>
                    ))}
                  </div>
                )}
                {(hasLoadedFullComments || (!isLoadingComments && comments.length > 0)) && comments.map((comment) => {
                  const commenter = getCommenter(comment.commentCognitoUserId);
                  const isOwnComment = comment.commentCognitoUserId === currentUser.cognitoUserId;
                  return (
                    <div key={comment.id} className="flex gap-3 group">
                      <div className={`h-7 w-7 rounded-full font-bold flex items-center justify-center text-[10px] flex-shrink-0 ${isOwnComment ? 'bg-blue-600 text-white shadow-sm' : 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400'}`}>
                        {commenter ? `${commenter.firstName.charAt(0)}${commenter.lastName.charAt(0)}` : '?'}
                      </div>
                      <div className={`flex-grow min-w-0 p-2.5 rounded-2xl rounded-tl-none relative ${
                        isOwnComment ? 'bg-blue-50 dark:bg-blue-900/40 border border-blue-100/50 dark:border-blue-800/50' : 
                        'bg-white dark:bg-slate-800 shadow-sm'
                      }`}>
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-[10px] text-slate-900 dark:text-white">{commenter ? `${commenter.firstName} ${commenter.lastName}` : 'Unknown Member'}</span>
                            {(isOwnComment || isAdmin) && (
                              <button 
                                onClick={() => setCommentToDelete(comment)} 
                                className={`p-1 transition-all ${
                                  isOwnComment 
                                    ? 'text-slate-400 md:hover:text-red-500' 
                                    : 'text-amber-500 md:hover:text-amber-600'
                                }`}
                                title={isOwnComment ? 'Delete Comment' : 'Admin: Delete Comment'}
                              >
                                {isOwnComment ? <TrashIcon className="h-3 w-3" /> : <ShieldExclamationIcon className="h-3.5 w-3.5" />}
                              </button>
                            )}
                          </div>
                          <span className="text-[9px] text-slate-400">{formatRelativeTime(comment.createdAtEpochMilli)}</span>
                        </div>
                        <p className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed break-words">{comment.content}</p>
                        {(comment.likeCount ?? 0) > 0 && (
                          <div className="mt-1 flex items-center gap-1 text-[9px] font-bold text-slate-400">
                            <HeartSolid className="h-2.5 w-2.5 text-rose-400" />
                            {comment.likeCount}
                          </div>
                        )}
                      </div>
                      <button onClick={() => handleCommentLikeToggle(comment)} className="self-center p-1.5">
                        {comment.isLiked ? <HeartSolid className="h-3.5 w-3.5 text-rose-500" /> : <HeartOutline className="h-3.5 w-3.5 text-slate-300 md:hover:text-rose-400 transition-colors" />}
                      </button>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={handleAddComment} className="pt-2">
                <div className="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                  <textarea
                    ref={commentInputRef}
                    placeholder="Write a comment..."
                    className="w-full pl-4 pr-12 py-3 bg-transparent border-none focus:ring-0 outline-none text-base sm:text-xs resize-none dark:text-white min-h-[45px] max-h-[120px]"
                    rows={1}
                    value={newComment}
                    onChange={e => {
                      setNewComment(e.target.value);
                      // Auto-expand height
                      e.target.style.height = 'inherit';
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    maxLength={500}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment(e as any);
                      }
                    }}
                  />
                  <div className="absolute right-10 bottom-3">
                    <span className={`text-[8px] font-black tracking-widest ${
                      newComment.length >= 450 ? 'text-red-500' : 'text-slate-400'
                    }`}>
                      {newComment.length}/500
                    </span>
                  </div>
                  <button type="submit" disabled={!newComment.trim() || isSubmitting || newComment.length > 500} className={`absolute right-1.5 bottom-1.5 p-2 rounded-xl transition-all ${!newComment.trim() || isSubmitting || newComment.length > 500 ? 'text-slate-300' : 'text-blue-600 md:hover:bg-blue-50 md:dark:hover:bg-blue-900/30'}`}>
                    {isSubmitting ? <div className="h-4 w-4 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" /> : <PaperAirplaneIcon className="h-4 w-4 transform rotate-90" />}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fullscreen Lightbox */}
      {createPortal(
        <AnimatePresence>
          {isFullscreenOpen && post.signedMediaUrls && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[500] bg-black flex flex-col overflow-hidden"
            >
              {/* Dedicated Header Bar */}
              <div className="w-full h-14 sm:h-20 flex items-center justify-between px-4 sm:px-8 bg-black/80 border-b border-white/5 z-[550] shrink-0">
                <div className="flex items-center gap-3">
                  <div className="text-white/40 text-[10px] sm:text-xs font-black uppercase tracking-widest">
                    {currentIndex + 1} / {post.s3MediaKeys!.length}
                  </div>
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFullscreenOpen(false);
                  }}
                  className="p-2 sm:p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-[background-color,transform] border border-white/10 active:scale-90"
                  aria-label="Close fullscreen"
                >
                  <XIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Swipeable Media Container */}
              <div className="flex-grow w-full relative flex items-center justify-center overflow-hidden bg-black" onClick={() => setIsFullscreenOpen(false)}>
                <motion.div 
                  drag={!isZoomed && !isPinching ? "x" : false} 
                  dragDirectionLock
                  dragConstraints={{ 
                    left: -(post.s3MediaKeys!.length - 1) * windowWidth, 
                    right: 0 
                  }}
                  dragElastic={0.15}
                  dragMomentum={false}
                  onDragEnd={(_, info) => {
                    const threshold = 40;
                    if (info.offset.x < -threshold) paginate(1);
                    else if (info.offset.x > threshold) paginate(-1);
                  }}
                  className="w-full h-full flex bg-black"
                  initial={{ x: -currentIndex * windowWidth }}
                  animate={{ x: -currentIndex * windowWidth }}
                  transition={{ 
                    type: 'spring', 
                    damping: 40, 
                    stiffness: 400, 
                    mass: 0.8,
                    bounce: 0 
                  }}
                >
                  {post.signedMediaUrls.map((url, index) => {
                    const key = post.s3MediaKeys![index];
                    const isMediaVideo = isVideo(key);
                    return (
                      <motion.div 
                        key={key} 
                        className="w-full h-full flex-shrink-0 flex items-center justify-center select-none bg-black"
                        onClick={(e) => e.stopPropagation()}
                        drag={!isZoomed && !isPinching ? "y" : false}
                        dragDirectionLock
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={0.1}
                        dragMomentum={false}
                        onDragEnd={(_, info) => {
                          if (Math.abs(info.offset.y) > 120) setIsFullscreenOpen(false);
                        }}
                      >
                        <div className="relative w-full h-full flex items-center justify-center pointer-events-auto bg-black">
                          {isMediaVideo ? (
                            <VideoPlayer 
                              url={url} 
                              isActive={currentIndex === index} 
                              isFullscreen={true} 
                              onFullscreenExit={() => setWindowWidth(window.innerWidth)}
                            />
                          ) : (
                            <TransformWrapper
                              onTransform={(ref) => setIsZoomed(ref.state.scale > 1)}
                              onPinchStart={() => setIsPinching(true)}
                              onPinchStop={() => {
                                // Small delay to prevent Framer Motion from picking up the end of the pinch as a drag
                                setTimeout(() => setIsPinching(false), 100);
                              }}
                              initialScale={1}
                              minScale={1}
                              maxScale={4}
                              centerOnInit
                              panning={{ disabled: !isZoomed }}
                            >
                              <TransformComponent
                                wrapperStyle={{ width: '100%', height: '100%' }}
                                contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                <PostImage 
                                  url={url} 
                                  s3Key={key} 
                                  isMobile={isMobile} 
                                  isLoaded={loadedImages.has(key)}
                                  onLoaded={(k) => setMediaLoaded(prev => new Set(prev).add(k))}
                                  hideBlur
                                />
                              </TransformComponent>
                            </TransformWrapper>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>

                {/* Explicit Navigation Controls - Desktop Optimized */}
                {post.s3MediaKeys!.length > 1 && (
                  <>
                    <button 
                      onClick={(e) => { e.stopPropagation(); paginate(-1); }}
                      className={`absolute left-4 top-1/2 -translate-y-1/2 p-4 bg-black/60 hover:bg-black/80 rounded-full text-white transition-[background-color,transform] border border-white/20 z-[510] shadow-2xl hidden sm:block active:scale-90 transform-gpu ${currentIndex === 0 ? 'opacity-20 cursor-not-allowed' : 'opacity-100'}`}
                    >
                      <ChevronLeftIcon className="h-8 w-8" />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); paginate(1); }}
                      className={`absolute right-4 top-1/2 -translate-y-1/2 p-4 bg-black/60 hover:bg-black/80 rounded-full text-white transition-[background-color,transform] border border-white/20 z-[510] shadow-2xl hidden sm:block active:scale-90 transform-gpu ${currentIndex === post.s3MediaKeys!.length - 1 ? 'opacity-20 cursor-not-allowed' : 'opacity-100'}`}
                    >
                      <ChevronRightIcon className="h-8 w-8" />
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Delete Speedbump */}
      {createPortal(
        <AnimatePresence>
          {commentToDelete && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-slate-900/80 sm:bg-slate-900/70 z-[330] flex items-center justify-center p-4" onClick={() => setCommentToDelete(null)}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} transition={{ type: 'tween', ease: 'easeOut', duration: 0.3 }} className="bg-white dark:bg-slate-800 w-full max-w-sm rounded-[2rem] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="p-6 text-center">
                  <div className={`h-14 w-14 rounded-full flex items-center justify-center mx-auto mb-4 ${commentToDelete.commentCognitoUserId === currentUser?.cognitoUserId ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                    {commentToDelete.commentCognitoUserId === currentUser?.cognitoUserId 
                      ? <TrashIcon className="h-7 w-7 text-red-600 dark:text-red-400" />
                      : <ShieldExclamationIcon className="h-7 w-7 text-amber-600 dark:text-amber-400" />
                    }
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                    {commentToDelete.commentCognitoUserId === currentUser?.cognitoUserId ? 'Delete Comment?' : 'Admin: Delete Comment?'}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
                    {commentToDelete.commentCognitoUserId === currentUser?.cognitoUserId 
                      ? 'Are you sure you want to remove this comment? This action cannot be undone.'
                      : 'You are deleting someone else\'s comment as an administrator. There could be personal repercussions for removing family content. This action cannot be undone.'
                    }
                  </p>
                  <div className="flex gap-3">
                    <button onClick={confirmDeleteComment} className="flex-1 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all shadow-sm active:scale-95">
                      {commentToDelete.commentCognitoUserId === currentUser?.cognitoUserId ? 'Delete' : 'Admin Delete'}
                    </button>
                    <button onClick={() => setCommentToDelete(null)} className="flex-1 py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl font-bold hover:bg-slate-200 dark:hover:bg-slate-600 transition-all active:scale-95">Cancel</button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </motion.div>
  );
});
